import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Lock, Link2, GripVertical, RotateCcw, LogOut, Type, Move, Image as ImageIcon, Maximize2, Users, Sun, Zap, Layers, PanelLeft, Table, DoorOpen, Sparkles, Calculator, MessageCircle, Shield, GraduationCap } from 'lucide-react';
import SectionsEditor from '../components/admin/SectionsEditor';
import TableRowsEditor from '../components/admin/TableRowsEditor';
import GatesEditor from '../components/admin/GatesEditor';
import AISettingsEditor from '../components/admin/AISettingsEditor';
import CustomNodesEditor from '../components/admin/CustomNodesEditor';
import ConnectionEditor from '../components/admin/ConnectionEditor';
import LabelEditor from '../components/admin/LabelEditor';
import PositionEditor from '../components/admin/PositionEditor';
import BackgroundLayersEditor from '../components/admin/BackgroundLayersEditor';
import SizeEditor from '../components/admin/SizeEditor';
// Legacy UserManager removed — use UsersEditor for portal-based user_admin management
import SunEditor from '../components/admin/SunEditor';
import AnimationEditor from '../components/admin/AnimationEditor';
import TitleEditor from '../components/admin/TitleEditor';
import FooterEditor from '../components/admin/FooterEditor';
import ChallengesEditor from '../components/admin/ChallengesEditor';
import ContactEditor from '../components/admin/ContactEditor';
import ConnectionGroupsEditor from '../components/admin/ConnectionGroupsEditor';
import PanelsEditor from '../components/admin/PanelsEditor';
import IPWhitelistEditor from '../components/admin/IPWhitelistEditor';
import LoginAttemptsViewer from '../components/admin/LoginAttemptsViewer';
import SiteVisitsViewer from '../components/admin/SiteVisitsViewer';
import SupportChatSettings from '../components/admin/SupportChatSettings';
import SupportInbox from '../components/admin/SupportInbox';
import StudentCredsEditor from '../components/admin/StudentCredsEditor';
import GridCellsEditor from '../components/admin/GridCellsEditor';
import EnergyCalcEditor from '../components/admin/EnergyCalcEditor';
import UsersEditor from '../components/admin/UsersEditor';
import UserAnalyticsWidget from '../components/admin/UserAnalyticsWidget';
import VariantsEditor from '../components/admin/VariantsEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ mode = 'master' }) => {
  const navigate = useNavigate();
  const isUserMode = mode === 'user';
  const tokenKey = isUserMode ? 'portal_token' : 'admin_token';
  const [token, setToken] = useState(localStorage.getItem(tokenKey) || '');
  const [username, setUsername] = useState(localStorage.getItem('admin_username') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [activeTab, setActiveTab] = useState('sections');
  const [userInfo, setUserInfo] = useState(null); // { role, slug, permissions } (user mode only)

  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [passMsg, setPassMsg] = useState('');

  // In master mode this is the page switcher value.  In user mode it's
  // pinned to `user_<slug>` after we know the user info.
  const [editingPage, setEditingPage] = useState(
    isUserMode ? '' : (localStorage.getItem('admin_editing_page') || 'main')
  );

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
    params: { page: editingPage },
  }), [token, editingPage]);

  // Refetch config when the page selector changes
  useEffect(() => {
    if (token && isLoggedIn) {
      axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: editingPage },
      }).then(res => setConfig(res.data)).catch(() => {});
    }
    localStorage.setItem('admin_editing_page', editingPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPage]);

  useEffect(() => {
    if (token) {
      // In user mode, fetch principal info first to lock down the config page
      if (isUserMode) {
        axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(async (meRes) => {
            if (meRes.data.role !== 'user_admin') {
              // Master admin logging in via /dashboard? send them home.
              navigate('/admin', { replace: true });
              return;
            }
            setUserInfo(meRes.data);
            setUsername(meRes.data.username || '');
            // NOTE: backend forces config to user's slug regardless of ?page= — safe.
            const cfgRes = await axios.get(`${API}/admin/config`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setIsLoggedIn(true);
            setConfig(cfgRes.data);
          })
          .catch(() => {
            localStorage.removeItem('portal_token');
            navigate('/portal', { replace: true });
          });
        return;
      }
      // master admin path (unchanged)
      axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: editingPage },
      })
        .then(res => {
          setIsLoggedIn(true);
          setConfig(res.data);
        })
        .catch(() => {
          setToken('');
          setUsername('');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_username');
        });
    } else if (isUserMode) {
      navigate('/portal', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username: loginUsername || 'admin',
        password,
      });
      const t = res.data.token;
      const u = res.data.username || loginUsername || 'admin';
      setToken(t);
      setUsername(u);
      localStorage.setItem('admin_token', t);
      localStorage.setItem('admin_username', u);
      setIsLoggedIn(true);
      const configRes = await axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${t}` },
        params: { page: editingPage },
      });
      setConfig(configRes.data);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Giriş uğursuz oldu');
    }
  };

  const handleLogout = () => {
    setToken('');
    setUsername('');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_role');
    localStorage.removeItem('portal_slug');
    localStorage.removeItem('portal_username');
    setIsLoggedIn(false);
    setConfig(null);
    if (isUserMode) navigate('/portal', { replace: true });
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await axios.put(`${API}/admin/config`, config, authHeaders());
      setConfig(res.data);
      setSaveMsg('Uğurla saxlanıldı!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Xəta: ' + (err.response?.data?.detail || 'Saxlama uğursuz'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Bütün parametrləri defolta qaytarmaq istəyirsiniz?')) return;
    setSaving(true);
    try {
      const defaultConfig = {
        section_order: ['matrix', 'table', 'special'],
        table_row_order: [0, 1, 2, 3],
        connections: [
          ['B','D'],['B','Zh'],['B','Z'],['D','Zh'],['D','I'],['D','A'],
          ['Zh','Z'],['Zh','I'],['Zh','L'],['Zh','G'],['Z','L'],['Z','V'],
          ['I','G'],['I','M'],['I','A'],['L','G'],['L','V'],['G','M'],['G','K'],
          ['M','A'],['M','K'],['A','K'],['A','E'],['K','V'],['K','E'],['V','E']
        ],
        node_labels: {
          A: {letter: 'А', desc: 'Gün'}, B: {letter: 'Б', desc: 'Ay'},
          V: {letter: 'В', desc: 'İl'}, G: {letter: 'Г', desc: 'Yekun'},
          D: {letter: 'Д', desc: 'NƏ1'}, E: {letter: 'Е', desc: 'NƏ2'},
          Zh: {letter: 'Ж', desc: 'NƏ3'}, Z: {letter: 'З', desc: 'NƏ4'},
          I: {letter: 'И', desc: 'KD1'}, K: {letter: 'К', desc: 'KD2'},
          L: {letter: 'Л', desc: 'KD3'}, M: {letter: 'М', desc: 'KD4'},
          HRT: {letter: 'HRT', desc: 'Heart'}, Man: {letter: 'Man', desc: 'Manipura'},
          SUN: {letter: '☀', desc: 'Sun'},
        },
        node_positions: {
          B:{x:300,y:30}, K:{x:300,y:100}, Zh:{x:300,y:165},
          D:{x:175,y:245}, Z:{x:425,y:245}, I:{x:85,y:285}, L:{x:515,y:285},
          G:{x:300,y:315}, M:{x:300,y:415}, A:{x:25,y:305}, V:{x:575,y:305},
          E:{x:300,y:560}, HRT:{x:300,y:240}, Man:{x:300,y:370},
          SUN:{x:520,y:50},
        },
        node_colors: {
          B:'#a855f7', K:'#6366f1', Zh:'#3b82f6', D:'#4f46e5', Z:'#4f46e5',
          I:'#0ea5e9', L:'#0ea5e9', G:'#22c55e', M:'#eab308',
          A:'#f97316', V:'#f97316', E:'#ef4444',
          HRT:'#10b981', Man:'#f59e0b', SUN:'#fbbf24',
        },
        node_sizes: {},
        global_sphere_size: 22,
        background_scale: 1,
        background_offset_x: 0,
        background_offset_y: 0,
        sun_x: 50,
        sun_y: -90,
        sun_scale: 1,
        sun_brightness: 1,
        connection_speed: 1,
        connection_style: 'straight',
      };
      const res = await axios.put(`${API}/admin/config`, defaultConfig, authHeaders());
      setConfig(res.data);
      setSaveMsg('Defolt parametrlər bərpa edildi!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Xəta baş verdi');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPassMsg('');
    try {
      await axios.put(`${API}/admin/password`, { old_password: oldPass, new_password: newPass }, authHeaders());
      setPassMsg('Parol uğurla dəyişdirildi!');
      setOldPass('');
      setNewPass('');
    } catch (err) {
      setPassMsg('Xəta: ' + (err.response?.data?.detail || 'Dəyişiklik uğursuz'));
    }
  };

  // Auto-save helper for quick-save tabs (sizes, background transform)
  const updateConfigField = (updates) => {
    setConfig({ ...config, ...updates });
  };

  // Save a partial set of fields directly to the backend (used for immediate-save fields like API keys)
  const saveFieldImmediate = async (updates) => {
    if (!config) return null;
    try {
      const res = await axios.put(`${API}/admin/config`, updates, authHeaders());
      setConfig(res.data);
      setSaveMsg('Yadda saxlandı');
      setTimeout(() => setSaveMsg(''), 2000);
      return res.data;
    } catch (err) {
      setSaveMsg('Xəta: ' + (err.response?.data?.detail || 'Saxlama uğursuz'));
      throw err;
    }
  };

  if (!isLoggedIn) {
    // user_admin mode always redirects to /portal (handled in the token effect)
    // so we skip the master admin login form and show a lightweight loader.
    if (isUserMode) {
      return (
        <div className="karma-page">
          <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 text-white/70">
            Yönləndirilir…
          </div>
        </div>
      );
    }
    return (
      <div className="karma-page">
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
          <div className="admin-login-card">
            <div className="flex items-center gap-3 mb-6">
              <Lock size={24} className="text-purple-400" />
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Cinzel', serif" }}>Admin Panel</h2>
            </div>
            <form onSubmit={handleLogin} className="flex flex-col gap-3">
              <input
                type="text"
                className="karma-input w-full text-left"
                placeholder="İstifadəçi adı (default: admin)"
                value={loginUsername}
                onChange={e => setLoginUsername(e.target.value)}
                autoFocus
                data-testid="login-username-input"
              />
              <input
                type="password"
                className="karma-input w-full"
                placeholder="Parol"
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
              {loginError && <p className="text-red-400 text-sm" data-testid="login-error">{loginError}</p>}
              <button type="submit" className="karma-button w-full" data-testid="login-submit-btn">Daxil ol</button>
            </form>
            <button
              onClick={() => navigate('/')}
              className="mt-4 text-sm text-white/40 hover:text-white/70 transition-colors flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Geri qayıt
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="karma-page">
      <div className="relative z-10 min-h-screen px-4 py-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="text-white/50 hover:text-white/80 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Cinzel', serif" }}>
              Admin Panel
            </h1>
            {username && (
              <span className="text-xs text-purple-300/70 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                {username}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {saveMsg && (
              <span className={`text-sm ${saveMsg.startsWith('X') ? 'text-red-400' : 'text-green-400'}`}>
                {saveMsg}
              </span>
            )}
            <button onClick={handleReset} className="admin-btn-secondary" title="Defolt parametrlər">
              <RotateCcw size={16} />
            </button>
            <button onClick={handleSave} disabled={saving} className="admin-btn-primary" data-testid="save-btn">
              <Save size={16} /> {saving ? 'Saxlanır...' : 'Saxla'}
            </button>
            <button onClick={handleLogout} className="admin-btn-secondary" title="Çıxış" data-testid="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Page selector — choose which page-config we're editing.
            "Main" affects the public homepage at /, "Student" affects the
            protected portal at /student.  Hidden in user_admin mode
            (they can only edit their own page). */}
        {isUserMode && userInfo && (
          <UserAnalyticsWidget
            token={token}
            slug={userInfo.slug}
            username={userInfo.username}
            isPublic={userInfo.is_public}
          />
        )}
        {!isUserMode && editingPage.includes('_var_') && (
          <>
          <div
            data-testid="variant-breadcrumb"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
              padding: '10px 14px',
              background: 'linear-gradient(90deg, rgba(6,182,212,0.12), rgba(59,130,246,0.08))',
              border: '1px solid rgba(6,182,212,0.4)', borderRadius: 10,
            }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>Variantı düzəldirsiniz:</span>
            <code style={{ fontSize: 12, color: '#67e8f9', fontFamily: 'monospace' }}>{editingPage}</code>
            <button
              onClick={() => {
                const parent = editingPage.split('_var_')[0];
                setEditingPage(parent);
                localStorage.setItem('admin_editing_page', parent);
              }}
              style={{
                marginLeft: 'auto', padding: '4px 10px', fontSize: 11,
                background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.4)',
                color: '#67e8f9', borderRadius: 6, cursor: 'pointer',
              }}>
              ← Parent-ə qayıt
            </button>
          </div>
          {config && (
            <div
              data-testid="variant-size-controls"
              style={{
                display: 'flex', alignItems: 'center', gap: 20, marginBottom: 18,
                padding: '12px 14px',
                background: 'rgba(168,85,247,0.06)',
                border: '1px solid rgba(168,85,247,0.25)', borderRadius: 10,
                flexWrap: 'wrap',
              }}>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>Bu variantın görünüşü:</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 240px' }}>
                <span style={{ fontSize: 12, color: '#c084fc', minWidth: 110 }}>
                  Max en: <b>{config.max_width || 800}px</b>
                </span>
                <input
                  type="range" min="300" max="1000" step="20"
                  value={config.max_width || 800}
                  onChange={(e) => updateConfigField({ max_width: parseInt(e.target.value, 10) })}
                  data-testid="variant-max-width-slider"
                  style={{ flex: 1 }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 220px' }}>
                <span style={{ fontSize: 12, color: '#c084fc', minWidth: 130 }}>
                  Başlıq ölçüsü: <b>{((config.title_scale || 1) * 100).toFixed(0)}%</b>
                </span>
                <input
                  type="range" min="0.5" max="2.5" step="0.1"
                  value={config.title_scale || 1}
                  onChange={(e) => updateConfigField({ title_scale: parseFloat(e.target.value) })}
                  data-testid="variant-title-scale-slider"
                  style={{ flex: 1 }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px' }}>
                <span style={{ fontSize: 12, color: '#c084fc', minWidth: 130 }}>Qrupların yeri:</span>
                <select
                  value={config.groups_position || 'below-grid'}
                  onChange={(e) => updateConfigField({ groups_position: e.target.value })}
                  data-testid="variant-groups-position"
                  style={{
                    flex: 1, padding: '4px 8px', fontSize: 12, borderRadius: 6,
                    background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(168,85,247,0.3)',
                    color: 'white',
                  }}
                >
                  <option value="above">Yuxarıda (Qruplar → Matrix → Grid)</option>
                  <option value="below-matrix">Matrix altında (Matrix → Qruplar → Grid)</option>
                  <option value="below-grid">Ən altda (Matrix → Grid → Qruplar)</option>
                </select>
              </label>
            </div>
          )}
          </>
        )}
        {!isUserMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 18,
            padding: '10px 14px',
            background: 'linear-gradient(90deg, rgba(168,85,247,0.08) 0%, rgba(109,40,217,0.06) 100%)',
            border: '1px solid rgba(168,85,247,0.25)',
            borderRadius: 10,
            flexWrap: 'wrap',
          }}
          data-testid="page-selector"
        >
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Düzəliş üçün səhifə:</div>
          {[
            { id: 'main', label: 'Ana səhifə (/)' },
            { id: 'student', label: 'Tələbə səhifəsi (/student)' },
          ].map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setEditingPage(p.id)}
              data-testid={`page-tab-${p.id}`}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: '1px solid',
                borderColor: editingPage === p.id ? '#a855f7' : 'rgba(255,255,255,0.1)',
                background: editingPage === p.id
                  ? 'linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)'
                  : 'rgba(255,255,255,0.04)',
                color: editingPage === p.id ? '#fff' : 'rgba(255,255,255,0.65)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {p.label}
            </button>
          ))}
          {editingPage === 'student' && (
            <span
              style={{
                fontSize: 11, color: '#fde68a',
                background: 'rgba(234,179,8,0.1)',
                padding: '3px 10px', borderRadius: 8,
                border: '1px solid rgba(234,179,8,0.3)',
              }}
            >
              ⚠ Yalnız tələbə səhifəsinə təsir edir
            </span>
          )}
        </div>
        )}

        {/* Compact page-level controls: same look as the variant control bar
            but scoped to the main / student page.  Only rendered when NOT
            editing a variant. */}
        {!isUserMode && !editingPage.includes('_var_') && config && (
          <div
            data-testid="page-level-controls"
            style={{
              display: 'flex', alignItems: 'center', gap: 20, marginBottom: 18,
              padding: '12px 14px',
              background: 'rgba(251,191,36,0.06)',
              border: '1px solid rgba(251,191,36,0.25)', borderRadius: 10,
              flexWrap: 'wrap',
            }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
              Bu səhifənin görünüşü:
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px' }}>
              <span style={{ fontSize: 12, color: '#fde68a', minWidth: 110 }}>Qrupların yeri:</span>
              <select
                value={config.groups_position || 'below-grid'}
                onChange={(e) => updateConfigField({ groups_position: e.target.value })}
                data-testid="page-groups-position"
                style={{
                  flex: 1, padding: '4px 8px', fontSize: 12, borderRadius: 6,
                  background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(251,191,36,0.3)',
                  color: 'white',
                }}>
                <option value="above">Yuxarıda (Qruplar → Matrix → Grid)</option>
                <option value="below-matrix">Matrix altında (Matrix → Qruplar → Grid)</option>
                <option value="below-grid">Ən altda (Matrix → Grid → Qruplar)</option>
              </select>
            </label>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 8,
                background: config.sum_enabled ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${config.sum_enabled ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.12)'}`,
                cursor: 'pointer', fontSize: 12,
                color: config.sum_enabled ? '#fde68a' : 'rgba(255,255,255,0.7)',
              }}
              title="İl Enerji Uyumu rejimində sferalar altında Σ = X etiketini göstər"
            >
              <input
                type="checkbox"
                checked={!!config.sum_enabled}
                onChange={(e) => updateConfigField({ sum_enabled: e.target.checked })}
                data-testid="page-sum-enabled"
                style={{ accentColor: '#fbbf24' }}
              />
              Σ Cəm dəyərləri
            </label>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 8,
                background: (config.sum_reduce22 !== false) ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${(config.sum_reduce22 !== false) ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.12)'}`,
                cursor: 'pointer', fontSize: 12,
                color: (config.sum_reduce22 !== false) ? '#fde68a' : 'rgba(255,255,255,0.7)',
                opacity: config.sum_enabled ? 1 : 0.55,
              }}
              title="Cəmə 22-qaydası tətbiq et (yandırıldıqda 35 → 13, söndürüldüktə xam cəm)"
            >
              <input
                type="checkbox"
                checked={config.sum_reduce22 !== false}
                onChange={(e) => updateConfigField({ sum_reduce22: e.target.checked })}
                data-testid="page-sum-reduce22"
                disabled={!config.sum_enabled}
                style={{ accentColor: '#fbbf24' }}
              />
              22-qaydası
            </label>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 8,
                background: (config.compat_visible !== false) ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${(config.compat_visible !== false) ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)'}`,
                cursor: 'pointer', fontSize: 12,
                color: (config.compat_visible !== false) ? '#86efac' : 'rgba(255,255,255,0.7)',
              }}
              title="İstifadəçiyə 'İl Enerji Uyumu' checkbox-u və 2-ci tarix inputu göstərilsin"
            >
              <input
                type="checkbox"
                checked={config.compat_visible !== false}
                onChange={(e) => updateConfigField({ compat_visible: e.target.checked })}
                data-testid="page-compat-visible"
                style={{ accentColor: '#22c55e' }}
              />
              İl Enerji Uyumu göstər
            </label>
          </div>
        )}

        <div className="admin-tabs">
          {(() => {
            const allTabs = [
              { id: 'sections', label: 'Bölmələr', icon: GripVertical, perm: 'sections' },
              { id: 'title', label: 'Başlıq', icon: Type, perm: 'sections' },
              { id: 'rows', label: 'Cədvəl sətirləri', icon: Table, perm: 'table' },
              { id: 'footer', label: 'Alt mətn', icon: Type, perm: 'sections' },
              { id: 'positions', label: 'Yerlər', icon: Move, perm: 'positions' },
              { id: 'sizes', label: 'Ölçülər', icon: Maximize2, perm: 'sizes' },
              { id: 'connections', label: 'Bağlantılar', icon: Link2, perm: 'connections' },
              { id: 'groups', label: 'Qruplar', icon: Layers, perm: 'connections' },
              { id: 'animation', label: 'Animasiya', icon: Zap, perm: 'colors' },
              { id: 'labels', label: 'Hərflər', icon: Type, perm: 'labels' },
              { id: 'background', label: 'Arxa fon', icon: ImageIcon, perm: 'background' },
              { id: 'panels', label: 'Panellər', icon: PanelLeft, perm: 'sections' },
              { id: 'gates', label: 'Qapılar', icon: DoorOpen, perm: 'gates' },
              { id: 'custom-nodes', label: 'Yeni dairlər', icon: Calculator, perm: 'grid' },
              { id: 'grid-cells', label: 'Şəbəkə', icon: Table, perm: 'grid' },
              { id: 'energy', label: 'Karmik Enerji', icon: Zap, perm: 'grid' },
              { id: 'variants', label: 'Variantlar', icon: Layers, perm: 'sections' },
              { id: 'ai', label: 'AI Şərh', icon: Sparkles, perm: 'ai' },
              { id: 'challenges', label: 'Challenge-lar', icon: Sparkles, perm: 'sections' },
              { id: 'contact', label: 'Əlaqə', icon: MessageCircle, perm: 'sections' },
              { id: 'support', label: 'Dəstək çatı', icon: MessageCircle, masterOnly: true },
              { id: 'sun', label: 'Günəş', icon: Sun, perm: 'colors' },
              { id: 'users', label: 'İstifadəçilər', icon: Users, masterOnly: true },
              { id: 'student', label: 'Tələbə girişi', icon: GraduationCap, masterOnly: true },
              { id: 'security', label: 'Təhlükəsizlik', icon: Shield, masterOnly: true },
              { id: 'password', label: 'Parol', icon: Lock },
            ];
            const perms = userInfo?.permissions || {};
            const visibleTabs = allTabs.filter((t) => {
              if (!isUserMode) return true;
              if (t.masterOnly) return false;
              if (!t.perm) return true; // password always visible
              return perms[t.perm] !== false;
            });
            return visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`admin-tab ${activeTab === tab.id ? 'admin-tab-active' : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={15} />
                {tab.label}
              </button>
            ));
          })()}
        </div>

        <div className="admin-content">
          {activeTab === 'sections' && config && (
            <SectionsEditor
              sections={config.sections || []}
              onChange={(newSections) => setConfig({ ...config, sections: newSections })}
            />
          )}

          {activeTab === 'title' && config && (
            <TitleEditor
              title={config.title || ''}
              subtitle={config.subtitle || ''}
              calculateButtonText={config.calculate_button_text || ''}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'footer' && config && (
            <FooterEditor
              footerText={config.footer_text || ''}
              footerX={config.footer_x !== undefined ? config.footer_x : 50}
              footerY={config.footer_y !== undefined ? config.footer_y : 95}
              footerVisible={config.footer_visible !== false}
              footerFontSize={config.footer_font_size || 14}
              footerColor={config.footer_color || '#cbd5e1'}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'challenges' && config && (
            <ChallengesEditor
              challenges={config.challenges || []}
              sectionTitle={config.challenge_section_title || {}}
              enabled={config.challenges_enabled !== false}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'contact' && config && (
            <ContactEditor
              whatsappNumber={config.whatsapp_number || ''}
              whatsappMessage={config.whatsapp_message || ''}
              whatsappLabel={config.whatsapp_label || 'WhatsApp'}
              whatsappEnabled={!!config.whatsapp_enabled}
              instagramUsername={config.instagram_username || ''}
              instagramEnabled={!!config.instagram_enabled}
              telegramUsername={config.telegram_username || ''}
              telegramEnabled={!!config.telegram_enabled}
              emailAddress={config.email_address || ''}
              emailSubject={config.email_subject || ''}
              emailEnabled={!!config.email_enabled}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'support' && config && (
            <>
              <SupportChatSettings
                config={config}
                token={token}
                onChange={(updates) => {
                  // Update UI immediately AND persist to backend in one go,
                  // so the user never has to remember to hit Saxla for these
                  // settings (avoids the "didn't save" confusion).
                  updateConfigField(updates);
                  saveFieldImmediate(updates).catch(() => {});
                }}
              />
              <SupportInbox token={token} />
            </>
          )}

          {activeTab === 'groups' && config && (
            <ConnectionGroupsEditor
              groups={config.connection_groups || []}
              nodeLabels={config.node_labels || {}}
              onChange={(grps) => updateConfigField({ connection_groups: grps })}
            />
          )}

          {activeTab === 'rows' && config && (
            <TableRowsEditor
              rowOrder={config.table_row_order}
              hiddenRows={config.hidden_rows || []}
              hiddenColumns={config.hidden_columns || []}
              customRows={config.custom_rows || []}
              onRowOrderChange={(o) => setConfig({ ...config, table_row_order: o })}
              onHiddenChange={(h) => setConfig({ ...config, hidden_rows: h })}
              onHiddenColumnsChange={(h) => setConfig({ ...config, hidden_columns: h })}
              onCustomChange={(c) => setConfig({ ...config, custom_rows: c })}
            />
          )}

          {activeTab === 'positions' && config && (
            <PositionEditor
              positions={config.node_positions || {}}
              colors={config.node_colors || {}}
              labels={config.node_labels || {}}
              connections={config.connections || []}
              nodeSizes={config.node_sizes || {}}
              globalSphereSize={config.global_sphere_size}
              backgroundLayers={config.background_layers || []}
              onChange={(newPos) => setConfig({ ...config, node_positions: newPos })}
            />
          )}

          {activeTab === 'sizes' && config && (
            <SizeEditor
              globalSize={config.global_sphere_size}
              nodeSizes={config.node_sizes || {}}
              nodeLabels={config.node_labels || {}}
              nodeColors={config.node_colors || {}}
              onGlobalChange={(v) => updateConfigField({ global_sphere_size: v })}
              onNodeSizeChange={(ns) => updateConfigField({ node_sizes: ns })}
            />
          )}

          {activeTab === 'connections' && config && (
            <ConnectionEditor
              connections={config.connections}
              nodeLabels={config.node_labels || {}}
              onChange={(newConns) => setConfig({ ...config, connections: newConns })}
            />
          )}

          {activeTab === 'animation' && config && (
            <AnimationEditor
              connectionSpeed={config.connection_speed || 1}
              connectionStyle={config.connection_style || 'straight'}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'labels' && config && (
            <LabelEditor
              nodeLabels={config.node_labels || {}}
              nodeColors={config.node_colors || {}}
              nodeTextColors={config.node_text_colors || {}}
              nodeShapes={config.node_shapes || {}}
              hiddenSpheres={config.hidden_spheres || []}
              hiddenValues={config.hidden_values || []}
              token={token}
              onLabelsChange={(newLabels) => setConfig({ ...config, node_labels: newLabels })}
              onColorsChange={(newColors) => setConfig({ ...config, node_colors: newColors })}
              onTextColorsChange={(newTextColors) => updateConfigField({ node_text_colors: newTextColors })}
              onShapesChange={(newShapes) => setConfig({ ...config, node_shapes: newShapes })}
              onHiddenSpheresChange={(list) => updateConfigField({ hidden_spheres: list })}
              onHiddenValuesChange={(list) => updateConfigField({ hidden_values: list })}
              onNodeAdded={async () => {
                const r = await axios.get(`${API}/admin/config`, authHeaders());
                setConfig(r.data);
              }}
              onNodeDeleted={async () => {
                const r = await axios.get(`${API}/admin/config`, authHeaders());
                setConfig(r.data);
              }}
            />
          )}

          {activeTab === 'background' && config && (
            <BackgroundLayersEditor
              layers={config.background_layers || []}
              token={token}
              editingPage={editingPage}
              onLayersChange={(layers) => updateConfigField({ background_layers: layers })}
            />
          )}

          {activeTab === 'panels' && config && (
            <PanelsEditor
              panels={config.panels}
              backgroundFullscreen={config.background_fullscreen}
              paradiseMode={config.paradise_mode}
              hideGroupsBar={config.hide_groups_bar}
              hideSidePanels={config.hide_side_panels}
              onPanelsChange={(p) => updateConfigField({ panels: p })}
              onFullscreenChange={(v) => updateConfigField({ background_fullscreen: v })}
              onParadiseModeChange={(v) => updateConfigField({ paradise_mode: v })}
              onHideGroupsBarChange={(v) => updateConfigField({ hide_groups_bar: v })}
              onHideSidePanelsChange={(v) => updateConfigField({ hide_side_panels: v })}
            />
          )}

          {activeTab === 'gates' && config && (
            <GatesEditor
              gates={config.gates}
              nodeLabels={config.node_labels || {}}
              backgroundLayers={config.background_layers || []}
              onChange={(g) => updateConfigField({ gates: g })}
            />
          )}

          {activeTab === 'custom-nodes' && config && (
            <CustomNodesEditor
              customNodes={config.custom_nodes || []}
              backgroundLayers={config.background_layers || []}
              hiddenSpheres={config.hidden_spheres || []}
              hiddenValues={config.hidden_values || []}
              onChange={(nodes) => updateConfigField({ custom_nodes: nodes })}
              onHiddenSpheresChange={(list) => updateConfigField({ hidden_spheres: list })}
              onHiddenValuesChange={(list) => updateConfigField({ hidden_values: list })}
            />
          )}

          {activeTab === 'grid-cells' && config && (
            <GridCellsEditor
              cells={config.grid_cells || []}
              nodeLabels={config.node_labels || {}}
              backgroundLayers={config.background_layers || []}
              gridBgImage={config.grid_bg_image || ''}
              gridTitle={config.grid_title || ''}
              gridPopupsEnabled={config.grid_popups_enabled !== false}
              onChange={(cells) => updateConfigField({ grid_cells: cells })}
              onBgImageChange={(v) => updateConfigField({ grid_bg_image: v })}
              onTitleChange={(v) => updateConfigField({ grid_title: v })}
              onPopupsEnabledChange={(v) => updateConfigField({ grid_popups_enabled: v })}
            />
          )}

          {activeTab === 'energy' && config && (
            <EnergyCalcEditor
              value={config.energy_calc}
              onChange={(v) => updateConfigField({ energy_calc: v })}
            />
          )}

          {activeTab === 'ai' && config && (
            <AISettingsEditor
              aiEnabled={config.ai_enabled !== false}
              aiProvider={config.ai_provider || 'openai'}
              aiModel={config.ai_model || 'gpt-5.2'}
              aiSystemPrompt={config.ai_system_prompt}
              aiCustomApiKeySet={!!config.ai_custom_api_key_set}
              aiCustomApiKeyPreview={config.ai_custom_api_key_preview || ''}
              onChange={(updates) => updateConfigField(updates)}
              onSaveImmediate={saveFieldImmediate}
            />
          )}

          {activeTab === 'sun' && config && (
            <SunEditor
              sunX={config.sun_x !== undefined ? config.sun_x : 50}
              sunY={config.sun_y !== undefined ? config.sun_y : -90}
              sunScale={config.sun_scale || 1}
              sunBrightness={config.sun_brightness || 1}
              onChange={(updates) => updateConfigField(updates)}
            />
          )}

          {activeTab === 'variants' && config && (
            <VariantsEditor
              token={token}
              parentPage={editingPage.includes('_var_') ? editingPage.split('_var_')[0] : editingPage}
              onEditVariant={(cfgId) => {
                setEditingPage(cfgId);
                localStorage.setItem('admin_editing_page', cfgId);
                setActiveTab('positions');
              }}
              onRefetchParent={() => {
                axios.get(`${API}/admin/config`, {
                  headers: { Authorization: `Bearer ${token}` },
                  params: { page: editingPage },
                }).then(res => setConfig(res.data));
              }}
            />
          )}

          {activeTab === 'users' && (
            <UsersEditor token={token} />
          )}

          {activeTab === 'student' && (
            <StudentCredsEditor token={token} config={config} />
          )}


          {activeTab === 'security' && config && (
            <>
              <SiteVisitsViewer token={token} />
              <IPWhitelistEditor
                allowedIps={config.allowed_ips || []}
                token={token}
                onChange={(ips) => updateConfigField({ allowed_ips: ips })}
              />
              <LoginAttemptsViewer token={token} allowedIps={config.allowed_ips || []} />
            </>
          )}

          {activeTab === 'password' && (
            <div className="admin-section">
              <h3 className="admin-section-title">Parol dəyişdir</h3>
              <p className="admin-section-desc">Cari istifadəçinin ({username || 'admin'}) parolunu dəyiş.</p>
              <form onSubmit={handlePasswordChange} className="flex flex-col gap-3 max-w-sm">
                <input
                  type="password"
                  className="karma-input w-full text-left"
                  placeholder="Köhnə parol"
                  value={oldPass}
                  onChange={e => setOldPass(e.target.value)}
                />
                <input
                  type="password"
                  className="karma-input w-full text-left"
                  placeholder="Yeni parol"
                  value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                />
                {passMsg && (
                  <p className={`text-sm ${passMsg.startsWith('X') ? 'text-red-400' : 'text-green-400'}`}>
                    {passMsg}
                  </p>
                )}
                <button type="submit" className="karma-button">
                  Parolu dəyiş
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
